import * as React from 'react';

import { enumSidecarSortOption } from '@rematter/frontend-lib-api-client';
import { Button, Col, ConsoleDialog, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { cameraCreateAtom } from '../../../_graphql/mutationCameraCreate';
import { sidecarsListQueryAtom } from '../../../_graphql/querySidecarsList';
import { useTranslations } from '../../../i18n/useTranslations';

interface DemoCameraCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const $sidecarsForPicker = sidecarsListQueryAtom(() => ({
  variables: { sorting: [enumSidecarSortOption.NameAsc], limit: 100 },
}));

export const DemoCameraCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: DemoCameraCreateDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [name, setName] = React.useState('');
  const [sidecarId, setSidecarId] = React.useState<string | null>(null);

  const createCamera = useSetAtom(cameraCreateAtom({}));
  const { data: sidecarsData } = useAtomValue($sidecarsForPicker);
  const sidecars = sidecarsData?.sidecars ?? [];

  const sidecarOptions = React.useMemo(
    () => sidecars.map((s) => ({ key: s.id, label: s.name })),
    [sidecars],
  );

  const canSubmit = name.trim() !== '' && sidecarId !== null;

  const resetForm = React.useCallback(() => {
    setName('');
    setSidecarId(null);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit || !sidecarId) return;

    try {
      setIsSubmitting(true);
      await createCamera({
        sidecarId,
        uniqueId: `demo:camera-${Date.now()}`,
        driverType: 'demo',
        name: name.trim(),
        streamProfile: 'sub',
      });
      toast.success(t.hardwareView.demoCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.hardwareView.demoCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    sidecarId,
    createCamera,
    name,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  const inputClass =
    'rounded border border-neutral/20 px-3 py-1.5 text-sm outline-none focus:border-primary';

  return (
    <ConsoleDialog
      className="@DemoCameraCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.hardwareView.demoCreateDialog.titles.camera}
      content={
        <Col gap3>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.demoCreateDialog.fields.name}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              disabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.demoCreateDialog.fields.sidecar}
            </label>
            <Select
              options={sidecarOptions}
              value={sidecarId}
              onChange={setSidecarId}
              placeholder={
                t.hardwareView.demoCreateDialog.fields.sidecarPlaceholder
              }
            />
          </Col>
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.hardwareView.demoCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.hardwareView.demoCreateDialog.actions.creating
              : t.hardwareView.demoCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
